import { AxiosError } from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@core/api";
import { ItemsAndType, SlimLineItem, EntityTag } from "@/apidtos";
import { useToast } from "@core/components/ui/use-toast";
import { useDialog } from "@core/components/ui/use-dialog";
import { useEcommerceConfig } from "@ecom/hooks/useEcommerceConfig";
import { useTranslation } from "react-i18next";
import { AddToCartMessageView } from "@ecom/components/AddToCartMessageView";
import { useSelectedPatient } from "@/overrides/med/components/SelectedPatientProvider";
import { AddToCartConfirmModal } from "@/overrides/ecommerce/components/AddToCartConfirmModal";
import { useCurrentUser } from "@core/hooks/useCurrentUser";
import { showLoginDialog } from "@core/components/forms/LoginModal";

export interface UseCurrentCartOptions {
  invalidation?: {
    onDelete?: {
      avoidList?: boolean;
      avoidSingle?: boolean;
    };
  };
  refetchOnWindowFocus?: boolean;
}

const PATIENT_TAG_KEY = "patient";
const ASSIGNED_PROVIDER_ID_TAG_KEY = "AssignedProviderId";

/**
 * Decorates a SlimLineItem with the currently-selected patient (from the
 * "Ordering for" dropdown) by:
 *   - tagging the line with `patient:{id}` so the backend can group/route it,
 *   - setting `ForceUniqueLineItemKey = "patient:{id}"` so the same product
 *     added for two different patients results in two separate cart lines,
 *     while the same product re-added for the same patient simply increments
 *     the existing line's quantity.
 *
 * Items added without a selected patient are left untouched.
 */
function applySelectedPatient(item: SlimLineItem, patientId: number | null): SlimLineItem {
  if (!patientId || patientId <= 0) {
    return item;
  }
  const tagValue = String(patientId);
  const existingTags: EntityTag[] = item.Tags ?? [];
  const hasPatientTag = existingTags.some(
    (t) => t.Key?.toLowerCase() === PATIENT_TAG_KEY,
  );
  const tags: EntityTag[] = hasPatientTag
    ? existingTags
    : [...existingTags, { Key: PATIENT_TAG_KEY, Value: tagValue } as EntityTag];

  return {
    ...item,
    Tags: tags,
    ForceUniqueLineItemKey: item.ForceUniqueLineItemKey ?? `patient:${tagValue}`,
  };
}

/**
 * Optionally stamps AssignedProviderId when the session already has an active
 * provider. Cart groups own the definitive selection (Fortius-style per-group
 * dropdown); this is only a default for the first add.
 */
function applyAssignedProvider(item: SlimLineItem, providerId: number | null | undefined): SlimLineItem {
  if (!providerId || providerId <= 0) {
    return item;
  }
  const existingTags: EntityTag[] = item.Tags ?? [];
  const hasTag = existingTags.some(
    (t) => t.Key === ASSIGNED_PROVIDER_ID_TAG_KEY,
  );
  if (hasTag) {
    return item;
  }
  return {
    ...item,
    Tags: [...existingTags, { Key: ASSIGNED_PROVIDER_ID_TAG_KEY, Value: String(providerId) } as EntityTag],
  };
}

export const useCurrentCart = (type: string, name?: string, options?: UseCurrentCartOptions) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { dialog } = useDialog();
  const config = useEcommerceConfig();
  const { patientId, patient } = useSelectedPatient();
  const { user } = useCurrentUser();

  const requireSignInBeforeAdd = (): boolean => {
    if (config.AllowGuestAddToCart) return false;
    if (user?.Id) return false;
    showLoginDialog();
    return true;
  };

  const api = useApi();
  const queryClient = useQueryClient();

  const providerContextQuery = useQuery({
    queryKey: ["providerContext"],
    queryFn: () => api.GetProviderContext(),
    staleTime: 60_000,
    enabled: !!user?.Id,
  });

  const decorateItems = (items: SlimLineItem[]): SlimLineItem[] =>
    items
      .map((it) => applySelectedPatient(it, patientId))
      .map((it) => applyAssignedProvider(it, providerContextQuery.data?.ActiveProviderId));

  const cartQuery = useQuery({
    queryKey: ["currentCart", type, name],
    queryFn: async () => {
      const result = await api["/cart/current"](type, name);
      return result;
    },
    refetchOnWindowFocus: options?.refetchOnWindowFocus != null ? options.refetchOnWindowFocus : true,
    refetchOnMount: true,
  });

  const reloadSingleCart = () => {
    queryClient.invalidateQueries({
      queryKey: ["currentCart", type, name]
    });
  };

  const reloadListCarts = () => {
    queryClient.invalidateQueries({
      queryKey: ["currentCart", "list", type]
    });
    queryClient.invalidateQueries({
      queryKey: ["currentCart", "list", type, name]
    });
  };

  const reloadAllCarts = () => {
    reloadSingleCart();
    reloadListCarts();
  };

  /**
   * Announces a successful add-to-cart via toast/dialog based on config and
   * reloads cart queries. Shared by both `addItems` and `addItemsDirect`.
   */
  const announceAddSuccess = (data: Awaited<ReturnType<typeof api.AddToCart>>) => {
    if (!data || data.length === 0) {
      return;
    }
    const messages = data
      .flatMap(item => item.Messages ?? [])
      .join(", ") || undefined;
    if (config.ShowDialogOnAddToCart[type]) {
      dialog({
        title: t("ui.cartUpdate", "Cart Update"),
        description: <AddToCartMessageView message={messages} type={type} />,
        action: <></>
      });
    }
    if (config.ShowToastOnAddToCart[type]) {
      toast({
        title: "Cart Update",
        description: messages || t("ui.addedToCart", "Item added to cart successfully."),
      });
    }
    reloadAllCarts();
  };

  const addItems = useMutation({
    mutationFn: async (items: SlimLineItem | SlimLineItem[]) => {
      if (requireSignInBeforeAdd()) {
        return [];
      }
      const arr = Array.isArray(items) ? items : [items];
      const decorated = decorateItems(arr);
      const dto: ItemsAndType = {
        Type: type,
        Name: name,
        Items: decorated,
      };
      return api.AddToCart(dto);
    },
    onSuccess: announceAddSuccess,
  });

  /**
   * Add to cart without showing the AddToCartConfirmModal. Use this when the
   * caller has already collected (and stamped on `Tags`) anything the modal
   * would have asked for — e.g. the Favorites grid where Directions is filled
   * in line. Patient decoration still runs.
   */
  const addItemsDirect = useMutation({
    mutationFn: async (items: SlimLineItem | SlimLineItem[]) => {
      if (requireSignInBeforeAdd()) {
        return [];
      }
      const arr = Array.isArray(items) ? items : [items];
      const decorated = decorateItems(arr);
      const dto: ItemsAndType = {
        Type: type,
        Name: name,
        Items: decorated,
      };
      return api.AddToCart(dto);
    },
    onSuccess: announceAddSuccess,
  });

  const updateItems = useMutation({
    mutationFn: (items: SlimLineItem | SlimLineItem[]) => {
      const dto: ItemsAndType = {
        Type: type,
        Name: name,
        Items: Array.isArray(items) ? items : [items]
      };
      return api.UpdateCartItemQuantity(dto);
    },
    onSuccess: () => {
      reloadAllCarts();
    }
  });

  const removeItems = useMutation({
    mutationFn: (itemIds: number[]) => api.UpdateCartItemQuantity({
      Type: type,
      Name: name,
      Items: itemIds.map(i => ({ ProductId: i, Quantity: 0 })),
    }),
    onSuccess: () => {
      reloadAllCarts();
    }
  });

  const clearCart = useMutation<void, AxiosError>({
    mutationFn: () =>
      api.ClearCart(type, name),
    onSuccess: () => {
      reloadAllCarts();
    },
    onError: (error) => {
      console.error(`Error clearing cart items: ${error.message}`);
      console.info(error);
    },
  });

  const deleteSalesCollectionForCurrentCustomer = useMutation<void, AxiosError, number>({
    mutationFn: (id: number) => api.DeleteSalesCollectionForCurrentCustomerById(id),
    onSuccess: () => {
      if (!options?.invalidation?.onDelete) {
        reloadAllCarts();
        return;
      }
      if (options.invalidation.onDelete.avoidList && options.invalidation.onDelete.avoidSingle) {
        return;
      }
      if (options.invalidation.onDelete.avoidList) {
        reloadSingleCart();
        return;
      }
      reloadListCarts();
    },
    onError: (error) => {
      console.error(`Error deleting sales collection: ${error.message}`);
      console.info(error);
    },
  });

  return {
    cartQuery,
    addItems,
    addItemsDirect,
    updateItems,
    removeItems,
    reloadCart: reloadAllCarts,
    clearCart,
    deleteSalesCollectionForCurrentCustomer,
  };
};
